import React, { useState } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { userAdminService } from '@services';
import ConfirmActionModal from '@components/admin/ConfirmActionModal';
import { formatPersonName } from '@utils/stringUtils';
import { showToast } from '@utils/toast';
import { getApiErrorMessage } from '@utils/apiError';
import type { ContactUserDeactivation, UseDeactivateContactUserOptions } from '@app-types/contactUserDeactivation';

/**
 * Deactivate a Contact-User: the button's disabled state and tooltip, and the confirmation.
 * Shared by the edit form and the detail page header so both follow the same rules.
 */
export const useDeactivateContactUser = ({
  user,
  onDeactivated
}: UseDeactivateContactUserOptions): ContactUserDeactivation => {
  const { user: currentUser } = useAuth();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  const isEditingSelf = user?.id !== undefined && user.id === currentUser?.id;
  const hasDependencies = Boolean(user?.dependencies?.hasDependencies);

  let tooltip = 'Deactivate User';
  if (isEditingSelf) tooltip = 'You cannot deactivate your own account';
  else if (hasDependencies) tooltip = 'Cannot deactivate: User has active dependencies';

  const handleConfirm = async () => {
    if (!user) return;
    setIsDeactivating(true);
    try {
      await userAdminService.updateUser(user.id, { is_active: false });
      setIsConfirmOpen(false);
      onDeactivated();
      showToast.success('User deactivated successfully');
    } catch (error) {
      console.error('Deactivate failed', error);
      showToast.error(getApiErrorMessage(error, 'Failed to deactivate user'));
    } finally {
      setIsDeactivating(false);
    }
  };

  const confirmModal = (
    <ConfirmActionModal
      isOpen={isConfirmOpen}
      onClose={() => setIsConfirmOpen(false)}
      onConfirm={handleConfirm}
      title="Deactivate User"
      message={`Are you sure you want to deactivate ${user ? formatPersonName(user) : 'this user'} (${user?.email})? They will no longer be able to sign in.`}
      isSubmitting={isDeactivating}
      actionType="deactivate"
    />
  );

  return {
    isDisabled: isDeactivating || isEditingSelf || hasDependencies,
    tooltip,
    openConfirm: () => setIsConfirmOpen(true),
    confirmModal
  };
};
